import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, ArrowLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SERVICE_DETAILS, getServiceBySlug } from "../data/serviceDetails";
import { INTEREST_OPTIONS } from "../data/content";

const toInterest = (title) =>
  INTEREST_OPTIONS.includes(title) ? title : title === "Enterprise SaaS Software" ? "Enterprise SaaS" : "";

const DEFAULT_TITLE = "Farmint | Enterprise Software & AI Engineering";
const DEFAULT_META = "Farmint builds enterprise software, SaaS platforms, web and mobile applications, AI solutions, cloud infrastructure and custom software for startups and enterprises.";

const NotFound = () => (
  <main className="mx-auto flex min-h-screen max-w-7xl flex-col items-center justify-center px-4 text-center" data-testid="service-not-found">
    <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary font-semibold">404</p>
    <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight">That service doesn&apos;t exist.</h1>
    <a href="/#services" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground" data-testid="not-found-back-btn">
      Explore all services <ArrowRight className="h-4 w-4" />
    </a>
  </main>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const index = SERVICE_DETAILS.findIndex((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    const meta = document.querySelector('meta[name="description"]');
    const prevMeta = meta?.getAttribute("content");
    if (service) {
      document.title = `${service.title} | Farmint`;
      if (meta) meta.setAttribute("content", service.meta);
    }
    return () => {
      document.title = DEFAULT_TITLE;
      if (meta && prevMeta) meta.setAttribute("content", prevMeta);
    };
  }, [service, slug]);

  if (!service) return <NotFound />;

  const related = service.related.map(getServiceBySlug).filter(Boolean);
  const interest = toInterest(service.title);
  const contactHref = interest ? `/?service=${encodeURIComponent(interest)}#contact` : "/#contact";

  return (
    <main className="relative" data-testid="service-detail-page">
      <div className="absolute inset-x-0 top-0 h-[420px] green-glow-bg" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16">
        <Reveal>
          <nav className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground" data-testid="service-breadcrumb" aria-label="Breadcrumb">
            <a href="/" className="transition-colors hover:text-primary">Home</a>
            <ChevronRight className="h-3 w-3" />
            <a href="/#services" className="transition-colors hover:text-primary">Services</a>
            <ChevronRight className="h-3 w-3" />
            <span className="text-primary">{service.title}</span>
          </nav>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                Service {String(index + 1).padStart(2, "0")} / {SERVICE_DETAILS.length}
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.06]" data-testid="service-title">
                {service.title}
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-4 font-display text-xl sm:text-2xl font-bold text-primary">{service.tagline}</p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground" data-testid="service-overview">
                {service.overview}
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={contactHref}
                  data-testid="service-cta-primary"
                  className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all duration-200 hover:brightness-110 hover:shadow-[0_0_32px_var(--green-glow)]"
                >
                  Start a Conversation
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
                <a
                  href="/#services"
                  data-testid="service-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border hairline px-6 py-3 text-sm font-semibold transition-colors duration-200 hover:border-primary/50 hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4" /> All services
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-16">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">What we deliver</h2>
              <ul className="mt-6 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                {service.deliver.map((d, i) => (
                  <li key={d} className="flex items-start gap-3 text-sm sm:text-base text-foreground/85" data-testid={`deliver-item-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    {d}
                  </li>
                ))}
              </ul>
            </Reveal>

            <div className="mt-16">
              <Reveal>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">How we approach it</h2>
              </Reveal>
              <div className="mt-8 space-y-0">
                {service.approach.map((step, i) => (
                  <Reveal key={step.title} delay={i * 0.07}>
                    <div className="relative flex gap-6 border-l hairline pb-10 pl-8 last:pb-0" data-testid={`approach-step-${i + 1}`}>
                      <span className="absolute -left-[7px] top-1.5 h-[13px] w-[13px] rounded-full border-2 border-primary bg-background" aria-hidden="true" />
                      <span className="font-mono text-sm font-semibold text-primary/80">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h3 className="font-display text-lg font-bold tracking-tight">{step.title}</h3>
                        <p className="mt-1 text-sm sm:text-base leading-relaxed text-muted-foreground">{step.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="space-y-5 lg:sticky lg:top-28">
              <Reveal delay={0.15}>
                <div className="rounded-2xl border hairline bg-card/60 p-7">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary font-semibold">Ideal for</p>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="ideal-for-chips">
                    {service.idealFor.map((chip) => (
                      <span key={chip} className="rounded-full border hairline bg-background/60 px-3.5 py-1.5 text-xs font-medium text-foreground/80">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.22}>
                <div className="rounded-2xl border hairline bg-card/60 p-7">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary font-semibold">Typical stack</p>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="stack-chips">
                    {service.stack.map((chip) => (
                      <span key={chip} className="rounded-full border hairline bg-background/60 px-3.5 py-1.5 font-mono text-[11px] text-muted-foreground">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.29}>
                <div className="rounded-2xl border border-primary/30 bg-primary/[0.05] p-7" data-testid="service-side-cta">
                  <p className="font-display text-lg font-bold tracking-tight">Have a {service.title.toLowerCase()} challenge?</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Tell us what you&apos;re building — we&apos;ll outline an engineering approach.
                  </p>
                  <a
                    href={contactHref}
                    data-testid="service-side-cta-btn"
                    className="group mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all duration-200 hover:brightness-110"
                  >
                    Talk to Farmint
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="mt-24 border-t hairline pt-14">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Related services</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <a
                  href={`/services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="group flex h-full flex-col justify-between rounded-2xl border hairline bg-card/60 p-6 transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-[0_8px_40px_var(--green-glow)]"
                >
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{r.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{r.tagline}</p>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.16em] text-primary">
                    Explore <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
